import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { 
  X, 
  Loader2,
  Crop as CropIcon,
  CheckCircle
} from 'lucide-react';

import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';


interface AvatarCropModalProps {
  isOpen: boolean;
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedFile: File) => void;
}

// Helper function to create circular crop
function centerCircleCrop(
  mediaWidth: number,
  mediaHeight: number,
) {
  const minSize = Math.min(mediaWidth, mediaHeight);
  const cropSize = Math.min(minSize * 0.8, 400); // Max 400px or 80% of smaller dimension
  
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: (cropSize / mediaWidth) * 100,
      },
      1, // 1:1 aspect ratio for circular crop
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

// Helper function to get cropped image blob
function getCroppedImg(image: HTMLImageElement, crop: PixelCrop): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Set canvas size to the crop size
  canvas.width = crop.width;
  canvas.height = crop.height;

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.95); // High quality JPEG
  });
}

export function AvatarCropModal({ isOpen, imageSrc, onClose, onCropComplete }: AvatarCropModalProps) {
  
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [processing, setProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);


  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerCircleCrop(width, height));
  }, []);

  const handleCropComplete = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      return;
    }

    try {
      setProcessing(true);
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      const croppedFile = new File([croppedBlob], 'avatar.jpg', {
        type: 'image/jpeg',
      });
      onCropComplete(croppedFile);
    } catch (err) {
      console.error('Failed to crop image:', err);
      alert('Failed to process cropped image');
    } finally {
      setProcessing(false);
    }
  }, [completedCrop, onCropComplete]);
  
  if (!isOpen) {
    return null;
  }

  
  // Test with a simple div first
  if (!document.body) {
    console.error('❌ document.body is not available!');
    return null;
  }

  // Use portal to render modal directly into body to avoid z-index issues
  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <CropIcon className="w-6 h-6 text-purple-600" />
              <h3 className="text-xl font-semibold text-gray-900">Crop Avatar</h3>
            </div>
            <button
              onClick={onClose}
              disabled={processing}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Crop Interface */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 flex flex-col items-center justify-center bg-gray-50 min-h-full">
              <div className="max-w-full max-h-[60vh] overflow-auto">
                {imageSrc && (
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                    minWidth={100}
                    minHeight={100}
                    circularCrop
                  >
                    <img
                      ref={imgRef}
                      alt="Crop avatar"
                      src={imageSrc}
                      onLoad={onImageLoad}
                      className="max-w-full h-auto"
                      style={{ maxHeight: '60vh' }}
                    />
                  </ReactCrop>
                )}
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Drag to adjust the crop area for your avatar. The image will be cropped to a circular shape.
                </p>
                <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                  <span>• Minimum size: 100x100px</span>
                  <span>• Aspect ratio: 1:1 (Square)</span>
                  <span>• Format: JPEG</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-white flex-shrink-0">
            <button
              onClick={onClose}
              disabled={processing}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCropComplete}
              disabled={processing || !completedCrop}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Apply Crop</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}